// What God Mode's routes return (apps/api/src/routes/god-mode.js).

import type { UnauthorisedTagUse } from '../../components/UnauthorisedTagsAlert'

export interface Client {
  id: string
  name: string
  product: 'grow' | 'packright' | null
  product_label: string | null
  licence: string | null
  licensed_since: string | null
  timezone: string | null
  region: string | null
  region_label: string | null
  created_at: string
  people: number
  sign_ins: number
  signed_in: number
  // Scanned tags another client owns (services/tag-sharing.js).
  unauthorised_tags: number
  // The oldest version of each app its phones should be on ("20261029A") on each platform, whether forced, the newest
  // seen there and how many people last used an older one (migrations 176, 177, 180). Absent from an API before 176;
  // one setting for both platforms from an API before 180.
  app_updates?: Record<UpdateApp, Record<UpdatePlatform, AppUpdate> | AppUpdate>
}

export type UpdateApp = 'crew' | 'field'
export const UPDATE_APPS: { k: UpdateApp; label: string }[] = [
  { k: 'crew', label: 'Crew' },
  { k: 'field', label: 'Field' },
]

// Each platform has its own minimum (180): a fix can reach one store before the other, or only one has the bug.
export type UpdatePlatform = 'ios' | 'android'
export const UPDATE_PLATFORMS: { k: UpdatePlatform; label: string }[] = [
  { k: 'ios', label: 'iPhone' },
  { k: 'android', label: 'Android' },
]

// One app's setting per platform, whatever shape the API sent.
export function appUpdatesOf(c: Pick<Client, 'app_updates'>, app: UpdateApp): Record<UpdatePlatform, AppUpdate> | undefined {
  const u = c.app_updates?.[app]
  if (!u) return undefined
  return 'min_version' in u ? { ios: u as AppUpdate, android: u as AppUpdate } : (u as Record<UpdatePlatform, AppUpdate>)
}

export interface AppUpdate {
  min_version: string | null
  force: boolean
  newest: string | null
  behind: number
}

// Dataphyll's own app version: the day of the build's last commit and a letter for that day's first, second...
// (codemagic.yaml). After Y comes ZA. The API checks the same (services/app-updates.js).
export const VERSION_RE = /^\d{8}Z*[A-Y]$/

export interface Detail extends Omit<Client, 'sign_ins'> {
  sign_ins: { email: string; role: string; signed_in: boolean; created_at: string }[]
  licence_history: { changed_at: string; known: boolean; licence: string | null; source: string }[]
  changes: { kind: string; actor: string | null; before: unknown; after: unknown; at: string }[]
  unauthorised_tag_use: UnauthorisedTagUse | null
  // The number every tag this client prints ends in (::<pairs>): its legacy
  // packhouse ID once linked, else its own block.
  tag_number: number | null
  tag_suffix: string | null
  legacy_packhouse_id: number | null
  legacy_name: string | null
}

export interface Product {
  k: 'grow' | 'packright'
  label: string
}

export interface ClientsResponse {
  clients: Client[]
  // The newest version of each app seen on each platform at any client: offered as the next minimum (180).
  newest?: Record<UpdateApp, Record<UpdatePlatform, string | null>>
  products: Product[]
  licences: string[]
  regions: { code: string; label: string; time_zones?: string[] }[]
}

export interface DetailResponse {
  client: Detail
  products: Product[]
  licences: string[]
}

// A tag used by two or more clients: its owner (the client whose number it
// ends in, a legacy packhouse not yet moved, else the first to use it) and
// every client that used it, owners first.
export interface SharedTagUse {
  tenant_id: string
  name: string
  owner: boolean
  printed: boolean
  printed_at: string | null
  first_scan: string | null
  scans: number
}

export interface SharedTag {
  code: string
  label: string
  basis: 'client' | 'legacy' | 'first_use'
  owners: { tenant_id: string | null; name: string; legacy_packhouse_id?: number }[]
  tenants: SharedTagUse[]
  unauthorised_scans: number
  last_first_scan: string | null
}

export interface SharedTagsResponse {
  tags: SharedTag[]
}

// The old system's clients, each with the CONTROL client it has moved to.
export interface LegacyPackhouse {
  packhouse_id: number
  name: string
  tag_suffix: string
  client: { id: string; name: string } | null
}

export interface LegacyPackhousesResponse {
  packhouses: LegacyPackhouse[]
}

export interface StockBatch {
  batch_id: string
  note: string | null
  created_at: string
  created_by: string | null
  count: number
  first: string
  last: string
  claimed: number
  unused: number
  claimed_by: { name: string; count: number }[]
}

export interface TagStockResponse {
  batches: StockBatch[]
  max: number
}

// A batch as it would be made: its range, the sheet's first and last rows,
// and the numbers passed over because their code is in use.
export interface StockPreview {
  count: number
  first: string | null
  last: string | null
  start: string
  width: number
  alphabet: 'alphanumeric' | 'numeric'
  head: { unique: string; qr: string }[]
  tail: { unique: string; qr: string }[]
  skipped: string[]
}

// Printed worker cards: a client's people, each with the card number every
// card of theirs names, and the batches made for them.
export interface CardPerson {
  id: string
  name: string
  employee_id: string | null
  card_number: number | null
  cards: number
}

export interface CardBatch {
  batch_id: string
  note: string | null
  created_at: string
  created_by: string | null
  count: number
  people: number
  first: string
  last: string
  names: string[]
}

export interface CardsResponse {
  client: { id: string; name: string }
  people: CardPerson[]
  batches: CardBatch[]
  max: number
  per_person_max: number
}

// A card batch as it would be made. A person with no card number yet has no
// QR DATA until the cards are made (qr is null).
export interface CardsPreview {
  count: number
  first: string | null
  last: string | null
  start: string
  width: number
  alphabet: 'alphanumeric' | 'numeric'
  people: { human_id: string; name: string; card_number: number | null; count: number; first: string; last: string }[]
  missing: string[]
  head: { unique: string; qr: string | null; name: string }[]
  tail: { unique: string; qr: string | null; name: string }[]
  skipped: string[]
}

export const licenceText = (l: string | null) => (l ? l[0].toUpperCase() + l.slice(1) : 'None')

export const dateText = (at: string | null) =>
  at ? new Date(at).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'

export const dateTimeText = (at: string | null) =>
  at ? new Date(at).toLocaleString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'

// A processing time or wait: "40 ms", "1.2 s", "12 s", "1.5 min", "25 min", "3.2 h".
const roughly = (n: number) => (n < 10 ? n.toFixed(1) : Math.round(n).toString())
export const durationText = (ms: number | null | undefined) =>
  ms === null || ms === undefined ? '—' : ms < 1000 ? `${Math.round(ms)} ms` : ms < 60_000 ? `${roughly(ms / 1000)} s` : ms < 3_600_000 ? `${roughly(ms / 60_000)} min` : `${roughly(ms / 3_600_000)} h`

// Client Setup (God Mode › client › Setup; API services/client-setup.js).
export interface SetupUse {
  id: string
  group: string
  label: string
  blurb: string
  locked?: boolean
  note?: string
  link?: string
}

export interface SetupQuestion {
  id: string
  label: string
  hint?: string
  multi?: boolean
  def?: string | string[]
  showIf?: [string, string]
  product?: string
  opts: string[]
}

export interface SetupEquipmentKind {
  id: string
  label: string
  needs: string
  versions: string[]
}

export interface SetupCatalogue {
  uses: SetupUse[]
  steps: Record<string, { title?: string; lead: string; qs: SetupQuestion[] }>
  general_qs: SetupQuestion[]
  equipment: SetupEquipmentKind[]
  moves: { k: EquipmentMove; label: string }[]
  licence: { terms: Opt[]; fee_per: Opt[]; currencies: string[] }
  team: { mode_uses: Record<string, string[]>; pay_answers: Record<string, string> }
}

export type SetupAnswers = Record<string, string | string[]>
type Opt = { k: string; label: string }
export type EquipmentMove = 'sent' | 'returned' | 'recovered' | 'swapped'

export interface ClientSetup {
  uses: string[]
  answers: SetupAnswers
  retain_from: string | null
  updated_at: string
  updated_by: string | null
}

export interface EquipmentRow {
  id: string
  kind: string
  version: string | null
  move: EquipmentMove
  qty: number
  moved_on: string
  serials: string[]
  serials_out: string[]
  work_order: string | null
  note: string | null
  created_at: string
  created_by: string | null
}

export interface SetupResponse {
  client: { id: string; name: string; product: string | null }
  setup: ClientSetup | null
  equipment: EquipmentRow[]
  held: Record<string, number>
  held_serials: Record<string, string[]>
  catalogue: SetupCatalogue
}

// The licence (God Mode › client › Setup › Licence; migration 183): recorded,
// not billed. Each field may be empty.
export interface LicenceTerms {
  work_order: string | null
  term: string | null
  starts_on: string | null
  ends_on: string | null
  sites: number | null
  peak_workers: number | null
  currency: string | null
  fee: number | null
  fee_per: string | null
  extra_worker_fee: number | null
  included: Record<string, number>
  note: string | null
}

export interface LicenceResponse {
  licence: (LicenceTerms & { history: { at: string; by: string | null }[]; updated_at: string; updated_by: string | null }) | null
  usage: {
    today: string
    sites: number
    days: { date: string; people: number }[]
    term: { from: string; to: string; busiest: { date: string; people: number } | null; days_over: number | null }
  }
  units: { kind: string; label: string; included: number; held: number; registered: number | null }[]
  held_serials: Record<string, string[]>
  devices: {
    id: string
    device_id: string
    name: string | null
    device_type: string | null
    active: boolean
    kind: string | null
    unit: { kind: string; serial: string; with_client: boolean } | null
  }[]
}

// GET /api/god-mode/dashboard (services/god-dashboard.js): each client today,
// each figure rated, and the API's own health.
export type Rating = 'ok' | 'idle' | 'warn' | 'bad'

export interface DashClient {
  id: string
  name: string
  demo: boolean
  timezone: string
  today: string
  local_time: string
  work_hours: boolean
  picks: { items: number; kg: number; pickers: number; last_pick_min: number | null }
  preassigns: { today: number; waiting: number }
  data: { last_min: number | null; crew_last_min: number | null; records_today: number; quality_today: number }
  people: { on_now: number; today: number; missing_clock_offs: number }
  phones: { platform: string; version: string | null; phones: number; last_hour: number }[]
  uploads_stuck: { name: string | null; platform: string; waiting: number; oldest_min: number }[]
  stations: { total: number; recent: number; seen_lately: number; last_min: number | null }
  wage_queue: { errors: number; stuck: number; queued: number; oldest_min: number | null; last_error: string | null }
  emails: { waiting: number; failed: number; last_error: string | null }
  backlog: { pending: number; failed: number; oldest_min: number | null; last_error: string | null }
  // Today's processing times (absent from an API before migration 191).
  timing?: {
    incoming: { timed: number; median_ms: number | null; max_ms: number | null; median_wait_s: number | null }
    wages: { done: number; average_ms: number | null; max_ms: number | null }
  }
  refusals: { last_day: number; phones: number; reason: string | null }
  photos_missing: { records: number; photos: number }
  unmatched_qc: number
  open_days: { days: number; oldest: string | null }
  status: Record<'data' | 'picks' | 'people' | 'phones' | 'uploads' | 'stations' | 'wages' | 'backlog' | 'refusals' | 'photos' | 'qc' | 'days' | 'overall', Rating>
  alerts: { status: Rating; key: string; text: string }[]
}

// GET /api/god-mode/queues: only the processing queues, which the dashboard
// asks for every few seconds so they count down live.
export type QueueFigures = Pick<DashClient, 'id' | 'wage_queue' | 'emails' | 'backlog'>

export interface QueuesResponse {
  at: string
  clients: QueueFigures[]
}

export interface DashboardResponse {
  at: string
  system: {
    api: 'ok'
    revision: string | null
    uptime_min: number
    db_ms: number
    live: boolean
    last_data_min: number | null
    read_ms: number
  }
  // The API's last hour, by every instance (absent from an API before migration 192).
  api?: { totals: ApiFigures; in_flight: ApiInFlight; slowest: ApiSlowRequest | null }
  // A store build number from before versions, or a version.
  newest_builds: Record<string, number | string>
  clients: DashClient[]
}

// GET /api/god-mode/system-load (services/system-load.js): the last hours of
// the API and the database from Cloud Monitoring, or why it cannot be read.
export interface LoadSeries {
  key: string
  label: string
  unit: string
  warn: number | null
  bad: number | null
  now: number | null
  peak: number | null
  status: Rating
  peak_status: Rating
  points: { t: string; v: number }[]
}

export type SystemLoadResponse =
  | { available: true; at: string; from: string; window_h: number; step_min: number; series: LoadSeries[] }
  | { available: false; reason: string }

// Queued email (God Mode › Queued email; API services/email-outbox.js, migration 171).
export type EmailStatus = 'queued' | 'sending' | 'retrying' | 'sent' | 'failed'

export interface EmailRow {
  id: string
  kind: string
  to_address: string
  subject: string
  status: EmailStatus
  attempts: number
  next_attempt_at: string | null
  last_error: string | null
  last_attempt_at: string | null
  sent_at: string | null
  retried_by: string | null
  created_at: string
  has_attachments?: boolean
  // The client it was sent for; null is System (Dataphyll's own).
  tenant: { id: string; name: string } | null
}

export interface EmailsResponse {
  emails: EmailRow[]
  counts: Record<EmailStatus, number>
  total: number
  page: number
  page_size: number
  kinds: string[]
  clients: { id: string; name: string }[]
}

export interface EmailDetail extends Omit<EmailRow, 'tenant' | 'has_attachments'> {
  text_body: string
  html_body: string | null
  attachments: string[]
}

// Refused Crew records at one client (API /api/god-mode/clients/:id/refusals).
export interface RefusalRow {
  record_id: string
  event_type: string | null
  reason: string
  crate_id: string | null
  tag_name: string | null
  tag_person: string | null
  sent_by: string | null
  tries: number
  first_at: string
  last_at: string
  taken: boolean
  // Refused quietly (POST …/refusals/quiet): the phone is told duplicate and lets it go.
  quiet: boolean
  // Control holds the whole record and the sign-in that sent it, so it can
  // send it through again itself (POST …/refusals/retry).
  kept: boolean
}

// QC scans no item took at one client (API /api/god-mode/clients/:id/qc-unmatched).
export interface UnmatchedQcRow {
  id: string
  at: string
  item_code: string | null
  quality_name: string | null
  qc_factor: number | null
  outcome: string | null
  reason: string | null
  rule: string | null
  candidates: number
  // Minutes from the scan to the nearest receipt of the item; negative when the receipt came first.
  nearest_min: number | null
}

export interface UnmatchedQcResponse {
  client: { id: string; name: string }
  days: number
  scans: UnmatchedQcRow[]
}

export interface RefusalsResponse {
  client: { id: string; name: string }
  days: number
  refusals: RefusalRow[]
}

export interface RefusalRetryResponse {
  tried: number
  taken: number
  refused: number
  reasons: Record<string, string>
}

export interface RefusalQuietResponse {
  quieted: number
}

// Queues (God Mode › Queues; API services/god-queues.js): incoming data and wage recalculations, one row per item.
export type QueueTab = 'incoming' | 'wages'
// God Mode's API page (API services/api-timing.js, migration 192).
export interface ApiFigures {
  n: number
  failed: number
  refused: number
  avg_ms: number | null
  median_ms: number | null
  p95_ms: number | null
  max_ms: number | null
}
export interface ApiInFlight { now: number; instances: number; oldest_ms: number | null; oldest_route: string | null }
export interface ApiSlowRequest {
  at: string
  method: string
  route: string
  status: number
  ms: number
  client?: { id: string; name: string | null } | null
}
export interface ApiHealthResponse {
  at: string
  hours: number
  windows: number[]
  totals: ApiFigures
  in_flight: ApiInFlight
  groups: (ApiFigures & { group: string; routes: (ApiFigures & { method: string; route: string })[] })[]
  slowest: ApiSlowRequest[]
}

// How a stretch of requests rates: failures first (5% or more is bad, any is a warning), then the 95th percentile.
export const apiRating = (f: ApiFigures): Rating =>
  !f.n ? 'idle' : f.failed / f.n >= 0.05 || (f.p95_ms ?? 0) >= 5000 ? 'bad' : f.failed || (f.p95_ms ?? 0) >= 1000 ? 'warn' : 'ok'

export type QueueStatus = 'waiting' | 'failed' | 'done'

export interface IncomingRow {
  status: QueueStatus
  id: string
  producer_id: string
  device_event_id: string
  attempts: number
  last_error: string | null
  last_attempt_at: string | null
  received_at: string
  done_at: string | null
  // The Cycle Engine's own time on it, its last try (null before migration 191).
  process_ms: number | null
  type: string | null
  item_code: string | null
  captured_at: string | null
  work_date: string | null
  human_id: string | null
  worker: string | null
  payload: Record<string, unknown>
  tenant: { id: string; name: string }
}

export interface WageRow {
  status: 'waiting' | 'failed'
  id: string
  state: string
  reason: string | null
  work_date: string | null
  requested_at: string
  requested_by: string | null
  processing_at: string | null
  attempt_count: number
  error: string | null
  human_id: string | null
  worker: string | null
  tenant: { id: string; name: string }
}

export interface QueueItemsResponse<Row = IncomingRow | WageRow> {
  tab: QueueTab
  items: Row[]
  counts: Partial<Record<QueueStatus, number>>
  total: number
  page: number
  page_size: number
  statuses: QueueStatus[]
  types: string[]
  producers: string[]
  // A priority client's incoming data is given the Cycle Engine's next turn ahead of the others'.
  clients: { id: string; name: string; priority: boolean }[]
  // Incoming data's average wait, received to done (one still waiting counts its wait so far), for the records received
  // in the past hour and in the clients' today, across the clients shown; avg_ms is null when none was received.
  waits: { hour: QueueWait; today: QueueWait }
}

export interface QueueWait {
  n: number
  avg_ms: number | null
}
